/**
 * Server-hosted, end-to-end encrypted chunked drops (Send Later).
 *
 * Plaintext files are encrypted in the browser with AES-GCM (256-bit) using
 * a key that lives solely in the URL fragment. Data is sliced into 1 MiB
 * blocks, framed with a 4-byte length prefix and 12-byte IV, and uploaded in
 * <= 32 MB chunks to /api/drop.
 *
 * Downloads stream block-by-block through Web Crypto into OPFS so multi-GB
 * files never accumulate in RAM.
 */

const BLOCK_SIZE = 1 * 1024 * 1024; // 1 MiB plaintext per block
const CHUNK_SIZE = 32 * 1024 * 1024; // 32 MB max per upload chunk

export interface DropUploadResult {
  id: string;
  url: string;
  key: string;
  expiresAt: number;
}

export interface DropServerMeta {
  id: string;
  name: string;
  size: number;
  mime: string;
  totalChunks: number;
  uploadedChunks: number[];
  expiresAt: number;
  downloads: number;
  maxDownloads: number;
}

export interface DropFileMeta {
  name: string;
  size: number;
  mime: string;
  blockSize: number;
  files?: { name: string; size: number; mime: string }[];
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function createDropKey(): Promise<{ key: CryptoKey; hex: string }> {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  const hex = bytesToHex(raw);
  const key = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
  return { key, hex };
}

export async function importDropKey(hex: string): Promise<CryptoKey> {
  if (!/^[a-f0-9]{64}$/i.test(hex)) throw new Error('This link is missing its encryption key. Ask the sender for the complete link.');
  const raw = hexToBytes(hex);
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, [
    'decrypt',
  ]);
}

/** Encrypt a slice into framed block: [4B len][12B IV][ciphertext + tag] */
async function encryptBlock(key: CryptoKey, plain: Uint8Array): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      plain as BufferSource,
    ),
  );
  const totalLen = iv.length + cipher.length;
  const out = new Uint8Array(4 + totalLen);
  new DataView(out.buffer).setUint32(0, totalLen);
  out.set(iv, 4);
  out.set(cipher, 16);
  return out;
}

export async function uploadDrop(
  input: File | File[],
  options?: {
    maxDownloads?: number;
    ttlMs?: number;
    onProgress?: (progress: number, bytesDone: number, totalBytes: number) => void;
  },
): Promise<DropUploadResult> {
  const files = Array.isArray(input) ? input : [input];
  if (!files.length) throw new Error('Choose at least one file.');
  const file = files.length === 1 ? files[0] : new File(files, 'Shared files', { type: 'application/octet-stream' });
  const { key, hex } = await createDropKey();

  const meta: DropFileMeta = {
    name: file.name,
    size: file.size,
    mime: file.type || 'application/octet-stream',
    blockSize: BLOCK_SIZE,
    files: files.map(f => ({ name: f.name, size: f.size, mime: f.type || 'application/octet-stream' })),
  };
  const metaBytes = new TextEncoder().encode(JSON.stringify(meta));
  if (metaBytes.length > 1024 * 1024) throw new Error('Too many filenames for one drop. Choose fewer files.');
  const headerBlock = await encryptBlock(key, metaBytes);

  const numDataBlocks = Math.ceil(file.size / BLOCK_SIZE);
  const totalEncryptedSize = headerBlock.length + file.size + 32 * numDataBlocks;

  const postRes = await fetch('/api/drop', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      name: 'encrypted.drop',
      size: totalEncryptedSize,
      mime: 'application/octet-stream',
      maxDownloads: options?.maxDownloads ?? 10,
      ttlMs: options?.ttlMs ?? 24 * 60 * 60_000,
    }),
  });

  if (!postRes.ok) {
    const err = await postRes.json().catch(() => ({}));
    throw new Error(err.error || `Drop upload failed (${postRes.status})`);
  }

  const { id, chunkSize = CHUNK_SIZE, totalChunks } = (await postRes.json()) as {
    id: string;
    chunkSize: number;
    totalChunks: number;
  };

  // Check already uploaded chunks for resumability
  const infoRes = await fetch(`/api/drop/${id}`);
  const existing = infoRes.ok
    ? ((await infoRes.json()) as DropServerMeta)
    : undefined;
  const uploadedSet = new Set(existing?.uploadedChunks ?? []);

  // Pre-generate all encrypted blocks as a generator or buffered stream
  let currentBlockIndex = 0;
  let currentBlock: Uint8Array | null = headerBlock;
  let blockOffset = 0;
  let totalUploaded = 0;

  async function nextEncryptedBytes(maxBytes: number): Promise<Uint8Array | null> {
    const parts: Uint8Array[] = [];
    let accumulated = 0;

    while (accumulated < maxBytes) {
      if (!currentBlock || blockOffset >= currentBlock.length) {
        if (currentBlockIndex >= numDataBlocks) break;
        const fileStart = currentBlockIndex * BLOCK_SIZE;
        const fileEnd = Math.min(file.size, (currentBlockIndex + 1) * BLOCK_SIZE);
        const slice = new Uint8Array(await file.slice(fileStart, fileEnd).arrayBuffer());
        currentBlock = await encryptBlock(key, slice);
        blockOffset = 0;
        currentBlockIndex++;
      }

      const available = currentBlock.length - blockOffset;
      const needed = maxBytes - accumulated;
      const take = Math.min(available, needed);

      parts.push(currentBlock.subarray(blockOffset, blockOffset + take));
      blockOffset += take;
      accumulated += take;
    }

    if (!parts.length) return null;
    if (parts.length === 1) return parts[0];

    const out = new Uint8Array(accumulated);
    let pos = 0;
    for (const p of parts) {
      out.set(p, pos);
      pos += p.length;
    }
    return out;
  }

  for (let c = 0; c < totalChunks; c++) {
    const expectedChunkSize = Math.min(chunkSize, totalEncryptedSize - c * chunkSize);
    const chunkData = await nextEncryptedBytes(expectedChunkSize);
    if (!chunkData) break;

    if (uploadedSet.has(c)) {
      totalUploaded += chunkData.length;
      options?.onProgress?.(totalUploaded / totalEncryptedSize, totalUploaded, totalEncryptedSize);
      continue;
    }

    const putRes = await fetch(`/api/drop/${id}/chunk/${c}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/octet-stream' },
      body: chunkData as unknown as BodyInit,
    });

    if (!putRes.ok) {
      throw new Error(`Failed to upload chunk ${c} (${putRes.status})`);
    }

    totalUploaded += chunkData.length;
    options?.onProgress?.(totalUploaded / totalEncryptedSize, totalUploaded, totalEncryptedSize);
  }

  const expiresAt = Date.now() + (options?.ttlMs ?? 24 * 60 * 60_000);
  const url = `${location.origin}/#drop=${id}&k=${hex}`;

  return { id, url, key: hex, expiresAt };
}

export async function fetchDropMeta(id: string): Promise<DropServerMeta> {
  const res = await fetch(`/api/drop/${id}`);
  if (!res.ok) throw new Error(res.status === 404 ? 'This drop has expired, been removed, or reached its download limit.' : 'Server error');
  return res.json() as Promise<DropServerMeta>;
}

/** Preview only the encrypted header; range requests do not consume a download. */
export async function previewDrop(id: string, keyHex: string): Promise<DropFileMeta> {
  const key = await importDropKey(keyHex);
  const readRange = async (start: number, end: number) => {
    const res = await fetch(`/api/drop/${id}/download`, { headers: { Range: `bytes=${start}-${end}` } });
    if (!res.ok) throw new Error(res.status === 404 ? 'This drop has expired or reached its download limit.' : 'This drop is still uploading. Try again shortly.');
    return new Uint8Array(await res.arrayBuffer());
  };
  const prefix = await readRange(0, 3);
  const length = new DataView(prefix.buffer).getUint32(0);
  if (length < 28 || length > 1024 * 1024 + 28) throw new Error('This drop is damaged. Ask the sender for a new link.');
  const payload = await readRange(4, length + 3);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: payload.subarray(0, 12) }, key, payload.subarray(12));
    return JSON.parse(new TextDecoder().decode(plain));
  } catch {
    throw new Error('This encryption key cannot open the drop. Ask the sender for the complete original link.');
  }
}

class ByteReader {
  private buffer = new Uint8Array(0);

  constructor(private readonly reader: ReadableStreamDefaultReader<Uint8Array>) {}

  async readExact(bytes: number): Promise<Uint8Array | null> {
    while (this.buffer.length < bytes) {
      const { done, value } = await this.reader.read();
      if (done) break;
      if (value) {
        const next = new Uint8Array(this.buffer.length + value.length);
        next.set(this.buffer);
        next.set(value, this.buffer.length);
        this.buffer = next;
      }
    }

    if (this.buffer.length < bytes) {
      if (this.buffer.length === 0) return null;
      throw new Error('Unexpected stream truncation');
    }

    const out = this.buffer.subarray(0, bytes);
    this.buffer = this.buffer.subarray(bytes);
    return out;
  }
}

export async function downloadDrop(
  id: string,
  keyHex: string,
  options?: {
    onProgress?: (progress: number, bytesDone: number, totalBytes: number) => void;
  },
): Promise<{ file: File; files: File[]; meta: DropFileMeta; expiresAt: number }> {
  const serverMeta = await fetchDropMeta(id);
  const cryptoKey = await importDropKey(keyHex);

  const dlRes = await fetch(`/api/drop/${id}/download`);
  if (!dlRes.ok || !dlRes.body) {
    throw new Error(`Download failed (${dlRes.status})`);
  }

  const reader = new ByteReader(dlRes.body.getReader());

  // 1. Read header block
  const headerLenBytes = await reader.readExact(4);
  if (!headerLenBytes) throw new Error('Empty drop stream');
  const headerLen = new DataView(headerLenBytes.buffer, headerLenBytes.byteOffset).getUint32(0);
  const headerPayload = await reader.readExact(headerLen);
  if (!headerPayload) throw new Error('Truncated header payload');

  const headerIv = headerPayload.subarray(0, 12);
  const headerCipher = headerPayload.subarray(12);
  const headerPlain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: headerIv as BufferSource },
    cryptoKey,
    headerCipher as BufferSource,
  );
  const meta: DropFileMeta = JSON.parse(new TextDecoder().decode(headerPlain));

  // 2. Set up OPFS stream if available, otherwise array of chunks
  let opfsWritable: FileSystemWritableFileStream | undefined;
  let opfsHandle: FileSystemFileHandle | undefined;
  const memoryChunks: BlobPart[] = [];

  if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
    try {
      const root = await navigator.storage.getDirectory();
      opfsHandle = await root.getFileHandle(`drop-enc-${id}`, { create: true });
      opfsWritable = await opfsHandle.createWritable();
    } catch {
      opfsWritable = undefined;
    }
  }

  // 3. Read data blocks
  let bytesDone = 0;
  while (true) {
    const lenBytes = await reader.readExact(4);
    if (!lenBytes) break;
    const len = new DataView(lenBytes.buffer, lenBytes.byteOffset).getUint32(0);
    const payload = await reader.readExact(len);
    if (!payload) throw new Error('Truncated data payload');

    const iv = payload.subarray(0, 12);
    const cipher = payload.subarray(12);
    const plain = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv as BufferSource },
        cryptoKey,
        cipher as BufferSource,
      ),
    );

    if (opfsWritable) {
      await opfsWritable.write(plain);
    } else {
      memoryChunks.push(plain as BlobPart);
    }

    bytesDone += plain.length;
    options?.onProgress?.(bytesDone / meta.size, bytesDone, meta.size);
  }

  let finalFile: File;
  if (opfsWritable && opfsHandle) {
    await opfsWritable.close();
    const diskFile = await opfsHandle.getFile();
    finalFile = new File([diskFile], meta.name, { type: meta.mime });
  } else {
    finalFile = new File(memoryChunks, meta.name, { type: meta.mime });
  }

  if (bytesDone !== meta.size) throw new Error('The download was interrupted. Please try again.');
  let offset = 0;
  const files = (meta.files || [{ name: meta.name, size: meta.size, mime: meta.mime }]).map(item => {
    const file = new File([finalFile.slice(offset, offset + item.size)], item.name, { type: item.mime });
    offset += item.size;
    return file;
  });
  if (offset !== meta.size) throw new Error('The file list is damaged. Ask the sender for a new drop.');
  return { file: finalFile, files, meta, expiresAt: serverMeta.expiresAt };
}
